import { createSelector } from 'reselect';
import { isEqual } from 'lodash';
import { entitiesFromStructure } from 'libs/utils/redux';
import { BookDetailStatus } from './constants';

const select = state => state.itemUpload;

const selectConfiguration = state => select(state).configuration;

const selectUi = state => select(state).ui;

const selectFormData = state => select(state).formData;

export const selectAttributes = state => select(state).attributes;
export const selectPreviousAttributes = state => select(state).previousAttributes;
export const getCatalogFieldsOrder = state => selectConfiguration(state).catalogFieldsOrder;
export const getMaxShippingPrice = state => selectConfiguration(state).maxShippingPrice;
export const getCurrency = state => selectConfiguration(state).currency;
export const getIsOfflineVerificationEligible = createSelector(select, _ref => {
  let {
    isOfflineVerificationEligible
  } = _ref;
  return isOfflineVerificationEligible;
});
export const getUiState = createSelector(selectUi, _ref2 => {
  let {
    uiState
  } = _ref2;
  return uiState;
});
export const getAssignedPhotos = createSelector(selectAttributes, _ref3 => {
  let {
    assignedPhotos
  } = _ref3;
  return assignedPhotos;
});
export const getIsOfflineVerificationModalOpen = createSelector(selectUi, _ref4 => {
  let {
    isOfflineVerificationModalOpen
  } = _ref4;
  return isOfflineVerificationModalOpen;
});
export const getContainsWebPhoto = createSelector(selectUi, _ref5 => {
  let {
    containsWebPhoto
  } = _ref5;
  return containsWebPhoto;
});
export const getIsFormDirty = createSelector(select, _ref6 => {
  let {
    attributes,
    previousAttributes
  } = _ref6;
  return !isEqual(attributes, previousAttributes);
});
export const getTempUuid = createSelector(selectFormData, _ref7 => {
  let {
    tempUuid
  } = _ref7;
  return tempUuid;
});
export const getIsBrandLuxury = state => selectFormData(state).brands.isLuxury;
export const getBrandTitle = state => selectAttributes(state).brandTitle;
export const getIsAuthenticityProofModalOpen = createSelector(selectUi, _ref8 => {
  let {
    isAuthenticityProofModalOpen
  } = _ref8;
  return isAuthenticityProofModalOpen;
});
export const getAuthenticityModalContent = createSelector(selectUi, _ref9 => {
  let {
    authenticityModal
  } = _ref9;
  return authenticityModal.content;
});
export const getIsAuthenticityModalOpen = createSelector(selectUi, _ref10 => {
  let {
    authenticityModal
  } = _ref10;
  return authenticityModal.isOpen;
});
export const getSuggestedBrands = createSelector(selectFormData, _ref11 => {
  let {
    brands
  } = _ref11;
  return entitiesFromStructure(brands.suggested);
});
export const getSuggestedColors = createSelector(selectFormData, _ref12 => {
  let {
    colors
  } = _ref12;
  return entitiesFromStructure(colors.suggested);
});
export const getSuggestedSizes = createSelector(selectFormData, _ref13 => {
  let {
    sizes
  } = _ref13;
  return entitiesFromStructure(sizes.suggested);
});
export const getBrands = createSelector(selectFormData, _ref14 => {
  let {
    brands
  } = _ref14;
  return entitiesFromStructure(brands);
});
export const getBrandById = (state, id) => selectFormData(state).brands.byId[id];
export function getCatalogPath(state, catalogId) {
  var _catalogs$byId$catalo;

  const {
    catalogs
  } = select(state).formData;
  const pathNames = [];
  let parentId = (_catalogs$byId$catalo = catalogs.byId[catalogId]) === null || _catalogs$byId$catalo === void 0 ? void 0 : _catalogs$byId$catalo.parentId;
  if (!parentId) return null;

  while (parentId) {
    var _catalogs$byId$parent;

    const parent = catalogs.byId[parentId];
    pathNames.unshift(parent.title);
    parentId = (_catalogs$byId$parent = catalogs.byId[parentId]) === null || _catalogs$byId$parent === void 0 ? void 0 : _catalogs$byId$parent.parentId;
  }

  return pathNames.join(' > ');
}
export function getSuggestedCatalogs(state) {
  const {
    catalogs
  } = select(state).formData;
  return catalogs.suggested.map(id => {
    const catalog = catalogs.byId[id];
    return {
      catalog,
      path: getCatalogPath(state, id)
    };
  }).filter(suggestion => suggestion.catalog || suggestion.path);
}
export function getCatalogsByIds(state, ids) {
  const {
    catalogs
  } = select(state).formData;
  const targetIds = ids !== null && ids !== void 0 && ids.length ? ids : catalogs.initialIds;
  return targetIds.map(id => catalogs.byId[id]);
}
export function getCatalog(state, id) {
  const {
    catalogs
  } = select(state).formData;
  if (!id) return null;
  return catalogs === null || catalogs === void 0 ? void 0 : catalogs.byId[id];
}
export function getChildCatalogsById(state, id) {
  var _parentCatalog$catalo;

  const parentCatalog = getCatalog(state, id);
  return getCatalogsByIds(state, (_parentCatalog$catalo = parentCatalog === null || parentCatalog === void 0 ? void 0 : parentCatalog.catalogIds) !== null && _parentCatalog$catalo !== void 0 ? _parentCatalog$catalo : null);
}
export function getCurrentCatalog(state) {
  const {
    catalogId
  } = selectAttributes(state);
  if (!catalogId) return null;
  return select(state).formData.catalogs.byId[catalogId];
}
export function getRestrictedToStatusId(state) {
  var _getCurrentCatalog;

  return (_getCurrentCatalog = getCurrentCatalog(state)) === null || _getCurrentCatalog === void 0 ? void 0 : _getCurrentCatalog.restrictedToStatusId;
}
export const getColors = createSelector(selectFormData, _ref15 => {
  let {
    colors
  } = _ref15;
  return entitiesFromStructure(colors);
});
export function getCurrentColors(state) {
  const {
    colors
  } = select(state).formData;
  const {
    colorIds
  } = selectAttributes(state);
  if (!colors.ids.length) return [];
  return colorIds.map(id => select(state).formData.colors.byId[id]);
}
export const getColorUiState = createSelector(selectUi, _ref16 => {
  let {
    color
  } = _ref16;
  return color.uiState;
});
export function getAvailableStatuses(state) {
  const {
    statuses
  } = select(state).formData;
  const currentCatalog = getCurrentCatalog(state);

  if (currentCatalog !== null && currentCatalog !== void 0 && currentCatalog.restrictedToStatusId) {
    return [statuses.byId[currentCatalog.restrictedToStatusId]];
  }

  return statuses === null || statuses === void 0 ? void 0 : statuses.ids.map(id => statuses.byId[id]);
}
export function getSize(state, id) {
  const {
    sizes
  } = select(state).formData;
  return sizes.byId[id];
}
export function getCurrentSize(state) {
  const {
    sizeId
  } = selectAttributes(state);
  if (!sizeId) return null;
  return getSize(state, sizeId);
}
export const getFieldError = (state, fieldName) => selectUi(state).errors.byName[fieldName] || undefined;
export const getFieldErrorSelector = fieldName => state => selectUi(state).errors.byName[fieldName] || undefined;
export const getIsShippingOptionsModalOpen = createSelector(selectUi, _ref17 => {
  let {
    shippingOptions
  } = _ref17;
  return shippingOptions.isModalOpen;
});
export const getIsLuxuryItemModalOpen = state => selectUi(state).isLuxuryItemModalOpen;
export const showMissingPostalCode = state => selectUi(state).showMissingPostalCode;
export const getShippingOptionsUiState = createSelector(selectUi, _ref18 => {
  let {
    shippingOptions
  } = _ref18;
  return shippingOptions.uiState;
});
export const getShippingOptions = createSelector(selectFormData, _ref19 => {
  let {
    shippingOptions
  } = _ref19;
  return shippingOptions;
});
export const getScreenName = createSelector(selectUi, _ref20 => {
  let {
    screenName
  } = _ref20;
  return screenName;
});
export const getPhotoTips = createSelector(selectFormData, _ref21 => {
  let {
    photoTips
  } = _ref21;
  return entitiesFromStructure(photoTips);
});
export function getCurrentSizeGroup(state) {
  const catalog = getCurrentCatalog(state);
  const {
    sizeGroups
  } = select(state).formData;

  if ((catalog === null || catalog === void 0 ? void 0 : catalog.sizeGroupId) === undefined || (catalog === null || catalog === void 0 ? void 0 : catalog.sizeGroupId) === null || !sizeGroups) {
    return null;
  }

  if (catalog.catalogIds.length !== 0) return null;
  return sizeGroups.byId[catalog.sizeGroupId];
}
export function getCurrentSizes(state) {
  const sizeGroup = getCurrentSizeGroup(state);
  if (!sizeGroup) return [];
  return sizeGroup.sizes;
}
export function getVideoGameRating(state, id) {
  const {
    videoGameRatings
  } = select(state).formData;
  return videoGameRatings === null || videoGameRatings === void 0 ? void 0 : videoGameRatings.byId[id];
}
export function getCurrentVideoGameRating(state) {
  const {
    videoGameRatingId
  } = selectAttributes(state);
  if (!videoGameRatingId) return null;
  return getVideoGameRating(state, videoGameRatingId);
}
export function getCurrentVideoGameRatings(state) {
  const {
    videoGameRatings
  } = select(state).formData;
  if (!videoGameRatings) return [];
  return videoGameRatings.ids.map(id => select(state).formData.videoGameRatings.byId[id]);
}
export const getIsPackageSizesVisible = createSelector(selectAttributes, _ref22 => {
  let {
    catalogId
  } = _ref22;
  return !!catalogId;
});
export const getIsFormDisabled = createSelector(selectUi, _ref23 => {
  let {
    isFormDisabled
  } = _ref23;
  return isFormDisabled;
});
export const getPriceSuggestions = createSelector(selectFormData, _ref24 => {
  let {
    priceSuggestions
  } = _ref24;
  return priceSuggestions;
});
export const getPriceSuggestionsMin = createSelector(getPriceSuggestions, _ref25 => {
  let {
    min
  } = _ref25;
  return min;
});
export const getPriceSuggestionsMax = createSelector(getPriceSuggestions, _ref26 => {
  let {
    max
  } = _ref26;
  return max;
});
export const getPriceSuggestionsCurrency = createSelector(getPriceSuggestions, _ref27 => {
  let {
    currency
  } = _ref27;
  return currency;
});
export const showSimilarItems = createSelector(selectUi, _ref28 => {
  let {
    price
  } = _ref28;
  return price.hasSimilarItems;
});
export const getIsSimilarItemsModalOpen = createSelector(selectUi, _ref29 => {
  let {
    price
  } = _ref29;
  return price.isSimilarItemsModalOpen;
});
export const getDisplayType = createSelector(selectUi, _ref30 => {
  let {
    price
  } = _ref30;
  return price.displayType;
});
export const getShowPriceSuggestions = state => {
  const min = getPriceSuggestionsMin(state);
  const max = getPriceSuggestionsMax(state);
  const {
    price
  } = selectAttributes(state);
  return max && min && price && price > max;
};
export function getCurrentStatus(state) {
  const {
    statusId
  } = selectAttributes(state);
  if (!statusId) return null;
  return select(state).formData.statuses.byId[statusId];
}
export function getStatus(state, id) {
  const {
    statuses
  } = select(state).formData;
  return statuses.byId[id];
}
export const getVisibilityFactory = function getVisibilityFactory(visibilityProp) {
  let defaultVisibility = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return createSelector(getCurrentCatalog, catalog => catalog ? catalog[visibilityProp] : defaultVisibility);
};
export const getIsBrandVisible = getVisibilityFactory('isBrandSelectShown', true);
export const getIsSizeVisible = getVisibilityFactory('isSizeSelectShown');
export const getIsColorVisible = getVisibilityFactory('isColorSelectShown');
export const getIsVideoGameRatingVisible = getVisibilityFactory('isVideoGameRatingSelectShown');
export const getIsIsbnVisible = getVisibilityFactory('isIsbnInputShown');
export const getIsAuthorVisible = getVisibilityFactory('isAuthorInputShown');
export const getIsBookTitleVisible = getVisibilityFactory('isBookTitleInputShown');
export const getIsMeasurementsVisible = getVisibilityFactory('isMeasurementsSelectShown');
export const getIsbnUiState = createSelector(selectUi, _ref31 => {
  let {
    isbn
  } = _ref31;
  return isbn.uiState;
});
export const getIsbnAuthor = createSelector(selectFormData, _ref32 => {
  let {
    bookDetails
  } = _ref32;
  return bookDetails ? bookDetails.author : null;
});
export const getIsbnBookTitle = createSelector(selectFormData, _ref33 => {
  let {
    bookDetails
  } = _ref33;
  return bookDetails ? bookDetails.title : null;
});
export function getBookDetailStatus(state) {
  const {
    bookDetails
  } = selectFormData(state);
  const isIsbnVisible = getIsIsbnVisible(state);
  if (!isIsbnVisible || !bookDetails) return null;
  if (bookDetails.author || bookDetails.title) return BookDetailStatus.Found;
  return BookDetailStatus.NotFound;
}
export const getCanBumpItem = createSelector(selectUi, _ref34 => {
  let {
    canBumpItem
  } = _ref34;
  return canBumpItem;
});
export const getDynamicAttributesSelector = fieldName => state => selectFormData(state).dynamicAttributes.byName[fieldName] || undefined;
export const getDynamicAttributesUiState = createSelector(selectUi, _ref35 => {
  let {
    dynamicAttributes
  } = _ref35;
  return dynamicAttributes.uiState;
});
export const getSelectedDynamicAttributeValue = (state, fieldName) => selectAttributes(state).catalog.attributes.byName[fieldName] || [];
export const getSelectedDynamicAttributes = createSelector(selectAttributes, _ref36 => {
  let {
    catalog
  } = _ref36;
  return catalog.attributes.names.map(name => ({
    field: name,
    value: catalog.attributes.byName[name]
  }));
});
export const getIsPackageSizeSelectedManually = state => {
  const {
    isPackageSizeSelectedManually
  } = select(state);
  return isPackageSizeSelectedManually;
};
export const getIsPromoted = createSelector(selectAttributes, _ref37 => {
  let {
    isPromoted
  } = _ref37;
  return isPromoted;
});
export const getAlertType = createSelector(selectAttributes, _ref38 => {
  let {
    alertType
  } = _ref38;
  return alertType;
});
export const getPreviousAlertType = createSelector(selectPreviousAttributes, _ref39 => {
  let {
    alertType
  } = _ref39;
  return alertType;
});
export const getFeedbackId = createSelector(selectAttributes, _ref40 => {
  let {
    feedbackId
  } = _ref40;
  return feedbackId;
});
export const getMeasurementWidth = createSelector(selectAttributes, _ref41 => {
  let {
    measurementWidth
  } = _ref41;
  return measurementWidth;
});
export const getMeasurementLength = createSelector(selectAttributes, _ref42 => {
  let {
    measurementLength
  } = _ref42;
  return measurementLength;
});
export const getShouldShowSizeModal = state => selectFormData(state).shouldShowSizeModal;
export const getIsPushUpChecked = createSelector(selectAttributes, _ref43 => {
  let {
    isPushUpChecked
  } = _ref43;
  return isPushUpChecked;
});
export const getIsSecondDayLister = createSelector(select, _ref44 => {
  let {
    isSecondDayLister
  } = _ref44;
  return isSecondDayLister;
});
export const getIsPreviousLister = createSelector(select, _ref45 => {
  let {
    isPreviousLister
  } = _ref45;
  return isPreviousLister;
});